import { EnumErrorLevel } from '../../../../../api/com/MetaPattern/Core/Infrastructure/Common/EnumErrorLevel';
import { ValidationResult } from '../../../../../api/com/MetaPattern/Core/Infrastructure/Common/ValidationResult';
import { APPComponent } from '../../../../../api/com/MetaPattern/MDA/MD/APPModel/APPComponent';
import { APP } from '../../../../../api/com/MetaPattern/MDA/MD/Base/APP';
import { BaseComponent } from '../../../../../api/com/MetaPattern/MDA/MD/Base/BaseComponent';
import { BaseElement } from '../../../../../api/com/MetaPattern/MDA/MD/Base/BaseElement';
import { Product } from '../../../../../api/com/MetaPattern/MDA/MD/Base/Product';
import { BEComponent } from '../../../../../api/com/MetaPattern/MDA/MD/EntityModel/BEComponent';
import { ProductComponent } from '../../../../../api/com/MetaPattern/MDA/MD/ProductModel/ProductComponent';
import { Dictionary } from '../../Product/IDE/Util/Dictionary';
import { ValidateHelper } from '../../Product/IDE/Util/ValidateHelper';
import { ValidationResultHelper } from '../Designer/Base/ValidationResultHelper';

/** 元数据校验 */
export class MDValidation {
  /**
   * 校验产品
   * 1. 必须填写名称
   * 2. 必须填写显示名称
   * @param component 应用组件
   * @param errors 错误信息
  */
  static validateProduct(product: Product, errors: Array<ValidationResult>) {
    this.validateBaseElement(product, product.designId, true, errors);
    if (!ValidateHelper.validateExpress('^[a-zA-Z]{1}[a-zA-Z0-9]{7,23}$', product.name)) {
      errors.push(ValidationResultHelper.createResult(EnumErrorLevel.Error, product.designId, product.designId, `产品 [${product.displayName}] 名称必须至少8位且包含字母和数字，以字母开头，最长24位`));
    }
    if (!ValidateHelper.IsNotNullOrEmpty(product.workScope_ID)) {
      errors.push(ValidationResultHelper.createResult(EnumErrorLevel.Error, product.designId, product.designId, `产品 [${product.displayName}] 必须设置工作空间`));
    }
    if (product.productComponents.length !== 1) {
      errors.push(ValidationResultHelper.createResult(EnumErrorLevel.Error, product.designId, product.designId, `产品 [${product.displayName}] 必须具备产品组件`));
    }
  }

  /**
   * 校验产品组件，校验规则
   * 1. 必须填写名称
   * 2. 必须填写显示名称
   * 3. 必须填写根名字空间
   * 4. 必须填写简称
   * 5. 产品应用校验
   * 5.1 产品应用必须设置对应的应用
   * 5.2 每个应用最多只能引用一次
   * @param component 组件
   * @param errors 错误
   */
  static validateProductComponent(component: ProductComponent, errors: Array<ValidationResult>) {
    this.validateBaseComponent(component, errors);
    component.productAPPs.forEach(productAPP => {
      if (productAPP.aPP === null) {
        errors.push(ValidationResultHelper.createResult(EnumErrorLevel.Error, productAPP.designId, productAPP.designId, `产品应用 [${productAPP.displayName}] 必须设置对应的应用`));
      } else {
        if (component.productAPPs.findIndex(item => item.id !== productAPP.id && item.aPP_ID === productAPP.aPP_ID) > -1) {
          errors.push(ValidationResultHelper.createResult(EnumErrorLevel.Error, productAPP.designId, productAPP.designId, '每个应用最多只能引用一次'));
        }
      }
    });
  }

  /**
   * 校验应用
   * 1. 必须填写名称
   * 2. 必须填写显示名称
   * 3. 必须设置工作空间
   * 4. 必须具备应用组件
   * 5. 必须具备APPKey
   * @param component 应用组件
   * @param errors 错误信息
  */
  static validateAPP(app: APP, errors: Array<ValidationResult>) {
    this.validateBaseElement(app, app.designId, true, errors);
    if (!ValidateHelper.validateExpress('^[a-zA-Z]{1}[a-zA-Z0-9]{5,23}$', app.name)) {
      errors.push(ValidationResultHelper.createResult(EnumErrorLevel.Error, app.designId, app.designId, `应用 [${app.displayName}] 名称必须至少6位且包含字母和数字，以字母开头，最长24位`));
    }
    if (!ValidateHelper.IsNotNullOrEmpty(app.workScope_ID)) {
      errors.push(ValidationResultHelper.createResult(EnumErrorLevel.Error, app.designId, app.designId, `应用 [${app.displayName}] 必须设置工作空间`));
    }
    if (app.aPPComponents.length !== 1) {
      errors.push(ValidationResultHelper.createResult(EnumErrorLevel.Error, app.designId, app.designId, `应用 [${app.displayName}] 必须具备应用组件`));
    }
    if (!ValidateHelper.IsNotNullOrEmpty(app.aPPKey)) {
      errors.push(ValidationResultHelper.createResult(EnumErrorLevel.Error, app.designId, app.designId, `应用 [${app.displayName}] 必须具备APPKey`));
    }
  }

  /**
   * 校验应用组件
   * 1. 必须填写名称
   * 2. 必须填写显示名称
   * 3. 必须填写根名字空间
   * 4. 必须填写简称
   * 5. 所有类型中，不能存在相同的名称
   * @param component 应用组件
   * @param errors 错误信息
  */
  static validateAPPComponent(component: APPComponent, errors: Array<ValidationResult>) {
    this.validateBaseComponent(component, errors);
    // 所有类型中，不能存在相同的名称
    const names = new Dictionary<string, number>();
    component.configGroups.forEach(element => this.appendTypeName(names, element.name));
    component.menuSets.forEach(element => this.appendTypeName(names, element.name));
    component.tasks.forEach(element => this.appendTypeName(names, element.name));
    names.Entries.forEach(entry => {
      if (entry.value > 1) {
        errors.push(ValidationResultHelper.createResult(EnumErrorLevel.Error, component.designId, component.designId, `组件 [${component.displayName}] 下不能具有同名类型：[${entry.key}]`));
      }
    });
  }

  /**
   * 校验应用组件
   * 1. 必须填写名称
   * 2. 必须填写显示名称
   * 3. 必须填写根名字空间
   * 4. 必须填写简称
   * 5. 所有类型中，不能存在相同的名称
   * @param component BE组件
   * @param errors 错误信息
  */
  static validateBEComponent(component: BEComponent, errors: Array<ValidationResult>) {
    this.validateBaseComponent(component, errors);
    // 所有类型中，不能存在相同的名称
    const names = new Dictionary<string, number>();
    component.businessLogics.forEach(element => this.appendTypeName(names, element.name));
    component.constants.forEach(element => this.appendTypeName(names, element.name));
    component.dTOes.forEach(element => this.appendTypeName(names, element.name));
    component.entities.forEach(element => this.appendTypeName(names, element.name));
    component.enums.forEach(element => this.appendTypeName(names, element.name));
    component.externalServices.forEach(element => this.appendTypeName(names, element.name));
    component.interfaces.forEach(element => this.appendTypeName(names, element.name));
    component.services.forEach(element => this.appendTypeName(names, element.name));
    component.valueObjects.forEach(element => this.appendTypeName(names, element.name));
    names.Entries.forEach(entry => {
      if (entry.value > 1) {
        errors.push(ValidationResultHelper.createResult(EnumErrorLevel.Error, component.designId, component.designId, `组件 [${component.displayName}] 下不能具有同名类型：[${entry.key}]`));
      }
    });
    // 外部服务，需要保持服务名称、服务地址的大小写敏感
    const serviceNames = new Array<string>();
    const serviceUrls = new Array<string>();
    let findIndex = 0;
    component.externalServices.forEach(es => {
      findIndex = serviceNames.findIndex(item => item.toLowerCase() === es.serviceName.toLocaleLowerCase());
      if (findIndex > -1 && serviceNames[findIndex] !== es.serviceName) {
        // 存在同名但大小写不一致的服务名称
        errors.push(ValidationResultHelper.createResult(EnumErrorLevel.Error, es.designId, es.designId, `外部服务 [${es.displayName}] 引用的服务名称与其他外部服务大小写不一致：[${es.serviceName}]`));
      } else {
        serviceNames.push(es.serviceName);
      }
      findIndex = serviceUrls.findIndex(item => item.toLowerCase() === es.serviceUrl.toLocaleLowerCase());
      if (findIndex > -1 && serviceUrls[findIndex] !== es.serviceUrl) {
        // 存在同名但大小写不一致的服务地址
        errors.push(ValidationResultHelper.createResult(EnumErrorLevel.Error, es.designId, es.designId, `外部服务 [${es.displayName}] 引用的服务地址与其他外部服务大小写不一致：[${es.serviceUrl}]`));
      } else {
        serviceUrls.push(es.serviceUrl);
      }
    });
  }

  private static appendTypeName(names: Dictionary<string, number>, typeName: string) {
    if (!names.contains(typeName)) {
      names.append(typeName, 0);
    }
    const useTimes = names.getValue(typeName);
    names.setValue(typeName, useTimes + 1);
  }

  /**
   * 校验组件基类，校验规则
   * 1. 必须填写名称
   * 2. 必须填写显示名称
   * 3. 必须填写根名字空间
   * 4. 必须填写简称
   * @param component 组件
   * @param errors 错误
   */
  private static validateBaseComponent(component: BaseComponent, errors: Array<ValidationResult>) {
    this.validateBaseElement(component, component.designId, true, errors);
    if (!ValidateHelper.IsNotNullOrEmpty(component.rootNamespace)) {
      errors.push(ValidationResultHelper.createResult(EnumErrorLevel.Error, component.designId, component.designId, `组件 [${component.displayName}] 必须填写根名字空间`));
    }
    if (!ValidateHelper.IsNotNullOrEmpty(component.alias) && !(component instanceof ProductComponent)) {
      errors.push(ValidationResultHelper.createResult(EnumErrorLevel.Error, component.designId, component.designId, `组件 [${component.displayName}] 必须填写简称`));
    }
  }

  /**
   * 校验元数据基类
   * 1. 必须填写名称
   * 2. 必须填写显示名称
   * @param element 元数据
   * @param containerDesignId 容器的设计期ID
   * @param isFirstUpper 是否以大写开头，false代表以小写开头
   * @param errors 错误
   */
  static validateBaseElement(element: BaseElement, containerDesignId: string, isFirstUpper: boolean, errors: Array<ValidationResult>) {
    if (isFirstUpper && !ValidateHelper.validateFirstIsUpper(element.name)) {
      errors.push(ValidationResultHelper.createResult(EnumErrorLevel.Error, containerDesignId, element.designId, `元素 [${element.displayName}] 的名称 [${element.name}] 必须以大写字母开头`));
    }
    if (!isFirstUpper && !ValidateHelper.validateFirstIsLower(element.name)) {
      errors.push(ValidationResultHelper.createResult(EnumErrorLevel.Error, containerDesignId, element.designId, `元素 [${element.displayName}] 的名称 [${element.name}] 必须以小写字母开头`));
    }
    if (!ValidateHelper.IsNotNullOrEmpty(element.name)) {
      errors.push(ValidationResultHelper.createResult(EnumErrorLevel.Error, containerDesignId, element.designId, `元素 [${element.displayName}] 必须填写名称`));
    }
    if (!ValidateHelper.IsNotNullOrEmpty(element.displayName)) {
      errors.push(ValidationResultHelper.createResult(EnumErrorLevel.Error, containerDesignId, element.designId, `元素 [${element.name}] 必须填写显示名称`));
    }
  }
}
